import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { Package, TrendingUp, ShoppingBag, AlertTriangle, ArrowRight, RefreshCw } from 'lucide-react';

function Stat({ label, value, sub, icon: Icon, tone = 'plum' }) {
  const bg = tone === 'plum' ? 'rgba(75,24,56,0.06)' : tone === 'green' ? 'rgba(91,110,44,0.1)' : 'rgba(154,75,126,0.1)';
  return (
    <Panel className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="h-eyebrow text-[10px]">{label}</div>
          <div className="h-display text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{value}</div>
          {sub && <div className="text-xs opacity-75 mt-1" style={{ color: 'var(--heritage-plum)' }}>{sub}</div>}
        </div>
        <div className="rounded-xl w-10 h-10 flex items-center justify-center" style={{ background: bg, color: 'var(--heritage-plum)' }}>
          <Icon size={18} />
        </div>
      </div>
    </Panel>
  );
}

export default function AdminShopDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/shop/dashboard');
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return <div className="h-eyebrow">Loading dashboard...</div>;
  }

  const s = data.stock;
  const o = data.orders;
  const r = data.revenue;

  return (
    <div className="space-y-5" data-testid="admin-shop-dashboard">
      <div className="h-panel-thin p-6 flex items-center justify-between">
        <div>
          <div className="h-eyebrow">Heritage Store</div>
          <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Store Dashboard</div>
          <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>Live view of stock, orders and revenue.</p>
        </div>
        <button onClick={load} className="h-btn-outline text-xs" data-testid="dashboard-refresh">
          <RefreshCw size={12} className="mr-1.5" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total stock units" value={s.total_units} sub={`${s.low_stock.length} low · ${s.out_of_stock.length} out`} icon={Package} />
        <Stat label="Orders (all time)" value={o.total} sub={`${o.this_week} this week`} icon={ShoppingBag} tone="mag" />
        <Stat label="Revenue (paid)" value={`$${r.all_time.toLocaleString()} ${r.currency}`} sub={`$${r.week.toLocaleString()} this week`} icon={TrendingUp} tone="green" />
        <Stat label="Pending fulfilment" value={(o.by_status?.paid) || 0} sub={`${(o.by_status?.shipped) || 0} shipped`} icon={AlertTriangle} tone="mag" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="h-eyebrow text-[10px]">Low stock</div>
              <div className="h-serif text-xl" style={{ color: 'var(--heritage-plum)' }}>Variants with 5 or fewer</div>
            </div>
            <Link to="/admin/store/products" className="h-btn-outline text-xs">Manage products <ArrowRight size={12} className="ml-1" /></Link>
          </div>
          {s.low_stock.length === 0 && s.out_of_stock.length === 0 && (
            <div className="text-sm opacity-75 mt-4" style={{ color: 'var(--heritage-plum)' }}>Everything is well-stocked.</div>
          )}
          <ul className="mt-3 divide-y" style={{ borderColor: 'rgba(75,24,56,0.15)' }} data-testid="low-stock-list">
            {s.out_of_stock.map((v) => (
              <li key={v.sku} className="py-2 flex items-center justify-between">
                <span className="text-sm" style={{ color: 'var(--heritage-plum)' }}>
                  {v.name} <span className="opacity-70">— {[v.size && `Size ${v.size}`, v.colour].filter(Boolean).join(' · ')}</span>
                </span>
                <span className="h-chip text-[10px]" style={{ background: 'rgba(154,44,44,0.15)', color: '#9A2C2C', borderColor: 'transparent' }}>Sold out</span>
              </li>
            ))}
            {s.low_stock.map((v) => (
              <li key={v.sku} className="py-2 flex items-center justify-between">
                <span className="text-sm" style={{ color: 'var(--heritage-plum)' }}>
                  {v.name} <span className="opacity-70">— {[v.size && `Size ${v.size}`, v.colour].filter(Boolean).join(' · ')}</span>
                </span>
                <span className="h-chip text-[10px]" style={{ background: 'rgba(154,75,126,0.15)', color: 'var(--heritage-plum)', borderColor: 'transparent' }}>{v.stock} left</span>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="h-eyebrow text-[10px]">Best sellers</div>
              <div className="h-serif text-xl" style={{ color: 'var(--heritage-plum)' }}>Top products by units</div>
            </div>
          </div>
          {data.top_sellers.length === 0 && (
            <div className="text-sm opacity-75 mt-4" style={{ color: 'var(--heritage-plum)' }}>No paid orders yet.</div>
          )}
          <ul className="mt-3 divide-y" style={{ borderColor: 'rgba(75,24,56,0.15)' }} data-testid="top-sellers-list">
            {data.top_sellers.map((p) => (
              <li key={p.slug} className="py-2 flex items-center justify-between text-sm" style={{ color: 'var(--heritage-plum)' }}>
                <span className="h-serif truncate mr-3">{p.name}</span>
                <span className="opacity-80 whitespace-nowrap">{p.units} sold · ${p.revenue.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
